import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { Alert, FormActions, FormDateInput, FormField, FormSelect, FormTextarea } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export interface ExternalInventoryItem {
  id: number;
  supplierName?: string;
  supplier_name?: string;
  wineName?: string;
  wine_name?: string;
  wineType?: string;
  wine_type?: string;
  grapeVariety?: string;
  grape_variety?: string;
  vintage?: number;
  bottleSize?: string;
  bottle_size?: string;
  quantity?: number;
  purchaseDate?: string;
  purchase_date?: string;
  purchasePrice?: number;
  purchase_price?: number;
  notes?: string;
  /** True when this external source has already been linked to main inventory (cannot link again). */
  linkedToInventory?: boolean;
  linked_to_inventory?: boolean;
}

const wineTypes = [
  { value: 'Red', label: 'Red' },
  { value: 'White', label: 'White' },
  { value: 'Rosé', label: 'Rosé' },
  { value: 'Sparkling', label: 'Sparkling' },
  { value: 'Other', label: 'Other' }
];

const bottleSizes = [
  { value: '375ml', label: '375ml' },
  { value: '750ml', label: '750ml' },
  { value: '1.5L', label: '1.5L' },
  { value: '3L', label: '3L' },
  { value: '5L', label: '5L' }
];

const schema = Yup.object().shape({
  supplierName: Yup.string().required('Supplier name is required'),
  wineName: Yup.string().required('Wine name is required'),
  wineType: Yup.string().required('Wine type is required'),
  bottleSize: Yup.string().required('Bottle size is required'),
  grapeVariety: Yup.string(),
  vintage: Yup.number().nullable(),
  quantity: Yup.number().integer().min(0).nullable(),
  purchaseDate: Yup.string().nullable(),
  purchasePrice: Yup.number().nullable(),
  notes: Yup.string()
});

/** Wine type to Tailwind classes (matches Bottling; Sparkling/Other use neutral). */
const getWineTypeTagClasses = (wineType: string | undefined | null): string => {
  if (!wineType) return 'bg-neutral-100 text-neutral-700';
  const t = String(wineType).trim().toLowerCase();
  if (t === 'red') return 'bg-red-100 text-red-700';
  if (t === 'white') return 'bg-yellow-100 text-yellow-700';
  if (t === 'rosé' || t === 'rose') return 'bg-pink-100 text-pink-700';
  if (t === 'orange') return 'bg-orange-100 text-orange-700';
  if (t === 'sparkling') return 'bg-amber-100 text-amber-700';
  return 'bg-neutral-100 text-neutral-700';
};

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';
  try {
    const d = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const date = new Date(d);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const ExternalInventory: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [items, setItems] = useState<ExternalInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ExternalInventoryItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkingId, setLinkingId] = useState<number | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/external-inventory');
      const raw = Array.isArray(res.data) ? res.data : [];
      setItems(
        raw.map((i: any) => ({
          ...i,
          linkedToInventory: i.linkedToInventory ?? i.linked_to_inventory ?? false,
        }))
      );
      setError(null);
    } catch (err: any) {
      setError(err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabVisible) fetchItems();
  }, [fetchItems, tabVisible]);

  const filtered = searchQuery.trim()
    ? items.filter(
        (i) =>
          (i.supplierName ?? i.supplier_name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (i.wineName ?? i.wine_name ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : items;

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        supplierName: values.supplierName,
        wineName: values.wineName,
        wineType: values.wineType,
        grapeVariety: values.grapeVariety || undefined,
        vintage: values.vintage ? Number(values.vintage) : undefined,
        bottleSize: values.bottleSize,
        quantity: values.quantity != null && values.quantity !== '' ? Number(values.quantity) : undefined,
        purchaseDate: values.purchaseDate || undefined,
        purchasePrice: values.purchasePrice ? Number(values.purchasePrice) : undefined,
        notes: values.notes || undefined
      };
      if (editingItem) {
        await api.put(`/api/external-inventory/${editingItem.id}`, payload);
        setSuccessMessage('External source updated.');
        fetchItems();
      } else {
        const res = await api.post('/api/external-inventory', payload);
        setSuccessMessage('External source added.');
        const newItem = res.data as ExternalInventoryItem;
        setItems((prev) => [newItem, ...prev]);
      }
      setModalOpen(false);
      setEditingItem(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err);
    }
  };

  const handleLinkToInventory = async (id: number) => {
    setLinkingId(id);
    try {
      const item = items.find((i) => i.id === id);
      const quantity = item?.quantity ?? 1;
      await api.post(`/api/external-inventory/${id}/link`, { quantity });
      setSuccessMessage('Linked to inventory.');
      setTimeout(() => setSuccessMessage(null), 3000);
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, linkedToInventory: true } : i))
      );
    } catch (err: any) {
      setError(err);
    } finally {
      setLinkingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    try {
      await api.delete(`/api/external-inventory/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setSuccessMessage('External source removed.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err);
    }
  };

  const openModal = (item: ExternalInventoryItem | null) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  if (loading && items.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {error && <ErrorMessageComponent error={error} />}
      {successMessage && (
        <Alert type="success" message={successMessage} autoHide onClose={() => setSuccessMessage(null)} />
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <input
          type="search"
          placeholder="Search supplier or wine..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:max-w-xs rounded-lg border border-monday-border px-3 py-2 text-sm"
        />
        <PermissionButton
          resource="external_inventory"
          action="create"
          onClick={() => openModal(null)}
          className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full bg-primary-600 text-white shadow-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          Add External Inventory
        </PermissionButton>
      </div>

      <section className="bg-white overflow-hidden shadow-md rounded-xl border border-monday-border-light">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-monday-text-muted">
            No external sources. Add one to track outsourced bottles.
          </div>
        ) : (
          <>
            <div className="px-4 py-3 sm:px-6 border-b border-monday-border-light bg-monday-background-subtle">
              <p className="text-sm font-medium text-monday-text-muted">
                {filtered.length} external source{filtered.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-monday-border-light">
                <thead>
                  <tr className="bg-monday-background-subtle">
                    <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider sm:pl-6">
                      Supplier
                    </th>
                    <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Wine Name
                    </th>
                    <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Wine Type
                    </th>
                    <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Vintage
                    </th>
                    <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Size
                    </th>
                    <th scope="col" className="py-3 px-3 text-center text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Quantity
                    </th>
                    <th scope="col" className="py-3 px-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th scope="col" className="py-3 px-3 text-right text-xs font-semibold text-monday-text-muted uppercase tracking-wider">
                      Price
                    </th>
                    <th scope="col" className="py-3 pl-3 pr-4 text-right text-xs font-semibold text-monday-text-muted uppercase tracking-wider sm:pr-6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-monday-border-light bg-white">
                  {filtered.map((item) => {
                    const wineType = item.wineType ?? item.wine_type;
                    return (
                      <tr key={item.id} className="hover:bg-monday-background-subtle/50 transition-colors">
                        <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                          {item.supplierName ?? item.supplier_name ?? '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text">
                          {item.wineName ?? item.wine_name ?? '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3">
                          {wineType ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWineTypeTagClasses(wineType)}`}>
                              {wineType}
                            </span>
                          ) : (
                            <span className="text-sm text-monday-text-muted">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text-muted">
                          {item.vintage ?? '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text-muted">
                          {item.bottleSize ?? item.bottle_size ?? '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text text-center">
                          {item.quantity != null ? item.quantity : '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text-muted">
                          {formatDate(item.purchaseDate ?? item.purchase_date ?? undefined)}
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 text-sm text-monday-text text-right">
                          {(item.purchasePrice ?? item.purchase_price) != null
                            ? `$${Number(item.purchasePrice ?? item.purchase_price).toFixed(2)}`
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm sm:pr-6">
                          <span className="inline-flex items-center gap-1">
                            {(item.linkedToInventory ?? item.linked_to_inventory) ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                                Linked
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleLinkToInventory(item.id)}
                                disabled={linkingId === item.id}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-60"
                              >
                                {linkingId === item.id ? 'Linking…' : 'Link to Inventory'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openModal(item)}
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-sky-700 bg-sky-100 hover:bg-sky-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1"
                            >
                              Edit
                            </button>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                              >
                                Delete
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <MobileModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} title={editingItem ? 'Edit External Source' : 'Add External Inventory'} size="xl">
        <Formik
          initialValues={{
            supplierName: editingItem?.supplierName ?? editingItem?.supplier_name ?? '',
            wineName: editingItem?.wineName ?? editingItem?.wine_name ?? '',
            wineType: editingItem?.wineType ?? editingItem?.wine_type ?? 'Red',
            grapeVariety: editingItem?.grapeVariety ?? editingItem?.grape_variety ?? '',
            vintage: editingItem?.vintage ?? '',
            bottleSize: editingItem?.bottleSize ?? editingItem?.bottle_size ?? '750ml',
            quantity: editingItem?.quantity ?? '',
            purchaseDate: editingItem?.purchaseDate ?? editingItem?.purchase_date ? new Date(editingItem.purchaseDate ?? editingItem.purchase_date!).toISOString().split('T')[0] : '',
            purchasePrice: editingItem?.purchasePrice ?? editingItem?.purchase_price ?? '',
            notes: editingItem?.notes ?? ''
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <FormField name="supplierName" label="Supplier Name" type="text" required />
              <FormField name="wineName" label="Wine Name" type="text" required />
              <FormSelect
                name="wineType"
                label="Wine Type"
                options={wineTypes}
                required
              />
              <FormField name="grapeVariety" label="Grape Variety" type="text" />
              <FormField name="vintage" label="Vintage (year)" type="number" placeholder="e.g. 2022" />
              <FormSelect name="bottleSize" label="Bottle Size" options={bottleSizes} required />
              <FormField name="quantity" label="Quantity (initial stock when linked)" type="number" placeholder="0" />
              <FormDateInput name="purchaseDate" label="Purchase Date" />
              <FormField name="purchasePrice" label="Purchase Price" type="number" placeholder="0.00" />
              <FormTextarea name="notes" label="Notes" rows={3} />
              <FormActions onCancel={() => { setModalOpen(false); setEditingItem(null); }} isSubmitting={isSubmitting} submitText={editingItem ? 'Update' : 'Save'} />
            </Form>
          )}
        </Formik>
      </MobileModal>
    </div>
  );
};

export default ExternalInventory;
